/**
 * Normalizes an uploaded wallpaper image to a fixed 1400x3100 canvas using
 * cover / crop-to-fill semantics, once, at upload time. The stored file IS
 * the final wallpaper — no on-the-fly resizing happens on view.
 *
 * Pass-through: anything that isn't a static image (live wallpaper video,
 * ringtone audio) or that fails to decode is returned unchanged so the
 * upload still succeeds (fail-open).
 */

import { detectContentType } from '@/utils/contentTypeDetect';

export const WALLPAPER_TARGET_WIDTH = 1400;
export const WALLPAPER_TARGET_HEIGHT = 3100;

const OUTPUT_MIME = 'image/jpeg';
const OUTPUT_QUALITY = 0.92;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image decode failed'));
    img.src = src;
  });
}

/**
 * Takes a File and returns a File normalized to exactly
 * WALLPAPER_TARGET_WIDTH x WALLPAPER_TARGET_HEIGHT via cover-crop-center.
 * Non-image files (video/audio) and anything unrecognized are returned as-is.
 * On any decode/canvas failure, logs and returns the original file so the
 * upload still succeeds.
 */
export async function processWallpaperImage(file: File): Promise<File> {
  // Only static images should be normalized — live wallpapers (video) and
  // ringtones (audio) must upload exactly as before.
  if (detectContentType(file) !== 'wallpaper') return file;

  let objectUrl: string | null = null;
  try {
    objectUrl = URL.createObjectURL(file);
    const image = await loadImage(objectUrl);

    const canvas = document.createElement('canvas');
    canvas.width = WALLPAPER_TARGET_WIDTH;
    canvas.height = WALLPAPER_TARGET_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context unavailable');
    ctx.imageSmoothingQuality = 'high';

    // Scale proportionally so the source fully covers the target box, then
    // center-crop the overflow. Never stretch/squash non-uniformly.
    const scale = Math.max(
      WALLPAPER_TARGET_WIDTH / image.width,
      WALLPAPER_TARGET_HEIGHT / image.height,
    );
    const scaledWidth = image.width * scale;
    const scaledHeight = image.height * scale;
    const dx = (WALLPAPER_TARGET_WIDTH - scaledWidth) / 2;
    const dy = (WALLPAPER_TARGET_HEIGHT - scaledHeight) / 2;

    ctx.drawImage(image, dx, dy, scaledWidth, scaledHeight);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, OUTPUT_MIME, OUTPUT_QUALITY);
    });
    if (!blob) throw new Error('Canvas toBlob failed');

    const baseName = file.name.replace(/\.[^/.]+$/, '');
    return new File([blob], `${baseName}.jpg`, { type: OUTPUT_MIME });
  } catch (err) {
    console.error('[processWallpaperImage] Failed to normalize image, uploading original file instead:', err);
    return file;
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}
